/**
 * Client-side image adjustments (brightness, contrast) for curves editing.
 * Uses canvas getImageData/putImageData; no backend. Clamps values 0–255.
 */

export interface ImageAdjustments {
  /** Brightness offset, typically -100 to +100 */
  brightness: number;
  /** Contrast factor, typically 0.5 to 2.0 (1 = no change) */
  contrast: number;
}

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

/**
 * Apply brightness and contrast to ImageData in place.
 * Brightness: add to R,G,B then clamp.
 * Contrast: multiply by factor around 1.0, centered at 128, then clamp.
 */
export function applyImageAdjustments(
  imageData: ImageData,
  { brightness, contrast }: ImageAdjustments
): void {
  const d = imageData.data;
  const n = d.length;

  for (let i = 0; i < n; i += 4) {
    let r = d[i];
    let g = d[i + 1];
    let b = d[i + 2];
    // Alpha unchanged

    // Brightness
    r = r + brightness;
    g = g + brightness;
    b = b + brightness;

    // Contrast (pivot 128)
    if (contrast !== 1) {
      r = (r - 128) * contrast + 128;
      g = (g - 128) * contrast + 128;
      b = (b - 128) * contrast + 128;
    }

    d[i] = clamp(Math.round(r), 0, 255);
    d[i + 1] = clamp(Math.round(g), 0, 255);
    d[i + 2] = clamp(Math.round(b), 0, 255);
  }
}

/**
 * Load image from URL, apply adjustments, return data URL (PNG).
 * Preserves exact dimensions. Uses a temporary canvas.
 */
export function applyAdjustmentsToImageUrl(
  imageUrl: string,
  adjustments: ImageAdjustments
): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas context failed'));
        return;
      }
      ctx.drawImage(img, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      applyImageAdjustments(imageData, adjustments);
      ctx.putImageData(imageData, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = imageUrl;
  });
}

/**
 * Load an image from any URL (data URL, blob URL, or http URL) and return an HTMLImageElement.
 */
function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${url.slice(0, 64)}…`));
    img.src = url;
  });
}

/**
 * Downsample an image into an ImageData buffer for analysis.
 * Returns null if the canvas context cannot be created.
 */
function downsampleToImageData(img: HTMLImageElement, sampleSize: number): ImageData | null {
  const w = img.naturalWidth;
  const h = img.naturalHeight;
  const scale = Math.min(1, sampleSize / Math.max(w, h));
  const sw = Math.max(1, Math.round(w * scale));
  const sh = Math.max(1, Math.round(h * scale));

  const canvas = document.createElement('canvas');
  canvas.width = sw;
  canvas.height = sh;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.imageSmoothingEnabled = true;
  ctx.drawImage(img, 0, 0, sw, sh);
  return ctx.getImageData(0, 0, sw, sh);
}

/**
 * Compute the mean R, G, B of pixels in `data` whose luminance falls in
 * [minLum, maxLum]. Skips transparent pixels and near-black padding.
 * Returns null when too few pixels match.
 */
function meanRgbInLuminanceBand(
  data: Uint8ClampedArray,
  minLum: number,
  maxLum: number,
  minPixels = 50
): [number, number, number] | null {
  let r = 0, g = 0, b = 0, n = 0;
  for (let i = 0; i < data.length; i += 4) {
    const a = data[i + 3];
    if (a < 8) continue;
    const pr = data[i], pg = data[i + 1], pb = data[i + 2];
    if (pr + pg + pb < 12) continue; // skip near-black padding
    // Rec. 709 luminance approximation.
    const y = 0.2126 * pr + 0.7152 * pg + 0.0722 * pb;
    if (y < minLum || y > maxLum) continue;
    r += pr; g += pg; b += pb; n++;
  }
  if (n < minPixels) return null;
  return [r / n, g / n, b / n];
}

/**
 * Compute mean R, G, B over all valid pixels (no luminance band).
 */
function meanRgbAll(data: Uint8ClampedArray): [number, number, number] {
  let r = 0, g = 0, b = 0, n = 0;
  for (let i = 0; i < data.length; i += 4) {
    const a = data[i + 3];
    if (a < 8) continue;
    const pr = data[i], pg = data[i + 1], pb = data[i + 2];
    if (pr + pg + pb < 12) continue;
    r += pr; g += pg; b += pb; n++;
  }
  if (n === 0) return [128, 128, 128];
  return [r / n, g / n, b / n];
}

/**
 * Match the white balance of `targetUrl` to the white balance of `referenceUrl`.
 *
 * Strategy: sample HIGHLIGHT pixels (bright walls, ceilings, sky, etc.) from
 * both images and compute per-channel gains from the means of those highlights.
 * Highlights are where chromatic casts read most strongly to the eye and tend
 * to be near-neutral surfaces in real-estate photos, making them the most
 * reliable cross-image color reference even when subject content differs
 * (e.g. clutter present in the reference, removed in the target).
 *
 * Falls back to the global mean if not enough highlight pixels are present.
 *
 * The gains are normalized by their geometric mean so overall luminance is
 * preserved (only the chromatic shift is corrected), then each gain is clamped
 * to ±maxGain and applied to the full-resolution result.
 */
export async function matchWhiteBalanceToReference(
  targetUrl: string,
  referenceUrl: string,
  options: { maxGain?: number; sampleSize?: number } = {}
): Promise<string> {
  const maxGain = options.maxGain ?? 1.2; // ±20% per channel max
  const sampleSize = options.sampleSize ?? 192;

  const [target, reference] = await Promise.all([
    loadImage(targetUrl),
    loadImage(referenceUrl),
  ]);

  const targetData = downsampleToImageData(target, sampleSize);
  const referenceData = downsampleToImageData(reference, sampleSize);
  if (!targetData || !referenceData) return targetUrl;

  // Prefer highlight band (bright but not blown-out) — typically walls/ceilings
  // in interior shots, where any chromatic cast is most visible.
  const HIGHLIGHT_MIN = 150;
  const HIGHLIGHT_MAX = 245;
  let tMean = meanRgbInLuminanceBand(targetData.data, HIGHLIGHT_MIN, HIGHLIGHT_MAX);
  let rMean = meanRgbInLuminanceBand(referenceData.data, HIGHLIGHT_MIN, HIGHLIGHT_MAX);

  // Fallback to midtones if there aren't enough highlights (e.g. dark rooms).
  if (!tMean || !rMean) {
    tMean = meanRgbInLuminanceBand(targetData.data, 80, HIGHLIGHT_MAX);
    rMean = meanRgbInLuminanceBand(referenceData.data, 80, HIGHLIGHT_MAX);
  }
  // Final fallback: full-image gray-world.
  if (!tMean) tMean = meanRgbAll(targetData.data);
  if (!rMean) rMean = meanRgbAll(referenceData.data);

  const [tr, tg, tb] = tMean;
  const [rr, rg, rb] = rMean;

  // Avoid division by zero on degenerate channels.
  const safe = (v: number) => Math.max(v, 1);
  let gainR = rr / safe(tr);
  let gainG = rg / safe(tg);
  let gainB = rb / safe(tb);

  // Preserve overall luminance: normalize gains by their geometric mean so we
  // only correct the chromatic shift, not the brightness.
  const geo = Math.cbrt(gainR * gainG * gainB);
  if (geo > 0) {
    gainR /= geo;
    gainG /= geo;
    gainB /= geo;
  }

  const minGain = 1 / maxGain;
  const clamp1 = (v: number) => Math.min(maxGain, Math.max(minGain, v));
  gainR = clamp1(gainR);
  gainG = clamp1(gainG);
  gainB = clamp1(gainB);

  // If the correction is essentially identity, skip the re-encode.
  const drift = Math.abs(gainR - 1) + Math.abs(gainG - 1) + Math.abs(gainB - 1);
  if (drift < 0.01) return targetUrl;

  const canvas = document.createElement('canvas');
  canvas.width = target.naturalWidth;
  canvas.height = target.naturalHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) return targetUrl;
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(target, 0, 0);
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const d = imageData.data;
  for (let i = 0; i < d.length; i += 4) {
    const r = d[i] * gainR;
    const g = d[i + 1] * gainG;
    const b = d[i + 2] * gainB;
    d[i] = r > 255 ? 255 : r < 0 ? 0 : r;
    d[i + 1] = g > 255 ? 255 : g < 0 ? 0 : g;
    d[i + 2] = b > 255 ? 255 : b < 0 ? 0 : b;
  }
  ctx.putImageData(imageData, 0, 0);
  return canvas.toDataURL('image/png');
}

/**
 * Same as applyAdjustmentsToImageUrl but returns a Blob (e.g. for upload).
 * Optional mimeType and quality for JPEG.
 */
export function applyAdjustmentsToImageUrlAsBlob(
  imageUrl: string,
  adjustments: ImageAdjustments,
  mimeType: 'image/png' | 'image/jpeg' = 'image/png',
  quality?: number
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas context failed'));
        return;
      }
      ctx.drawImage(img, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      applyImageAdjustments(imageData, adjustments);
      ctx.putImageData(imageData, 0, 0);
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error('toBlob failed'))),
        mimeType,
        quality
      );
    };
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = imageUrl;
  });
}
